import { LocaleLink } from "@/components/LocaleLink";
import { useLocale } from "@/lib/i18n";
import type { BlogPost } from "@/lib/blog-posts";
import { getBlogTheme } from "@/lib/blog-themes";

/** Post teaser: portrait cover, theme · date · reading time, title and excerpt. */
export function BlogPostCard({ post }: { post: BlogPost }) {
  const locale = useLocale();
  const c = locale === "da" ? post.da : post.en;
  const theme = getBlogTheme(post.theme);
  const date = new Date(post.publishedAt).toLocaleDateString(
    locale === "da" ? "da-DK" : "en-GB",
    { year: "numeric", month: "long", day: "numeric" },
  );
  const minLabel = locale === "da" ? "min." : "min";

  return (
    <LocaleLink
      to="/blog/$slug/$post"
      params={{ slug: post.theme, post: post.slug }}
      className="group block"
    >
      <div className="aspect-[4/5] overflow-hidden rounded-sm bg-muted">
        <img
          src={post.cover}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
      </div>
      <div className="mt-5">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {theme && <span className="text-terracotta">{theme[locale].label}</span>}
          {theme && <span aria-hidden>·</span>}
          <span>{date}</span>
          <span aria-hidden>·</span>
          <span>
            {post.readingMinutes} {minLabel}
          </span>
        </div>
        <h3 className="mt-3 font-display text-2xl leading-tight text-foreground transition-colors group-hover:text-terracotta">
          {c.title}
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground/70">{c.excerpt}</p>
      </div>
    </LocaleLink>
  );
}
